import { addDays, format, isValid, parseISO } from 'date-fns';
import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import type { ApiAdminpurchaseProofsGetCollectionParams } from '@r/api/generated/client.schemas.ts';

type BuildPurchaseProofsParamsInput = {
  columnFilters: ColumnFiltersState;
  sorting: SortingState;
  page: number;
  perPage: number;
};

/** Converts table pagination, filters, and sorting into purchase-proof API parameters. */
export function buildPurchaseProofsParams({
  columnFilters,
  sorting,
  page,
  perPage
}: BuildPurchaseProofsParamsInput): ApiAdminpurchaseProofsGetCollectionParams {
  /** Reads a string filter value from the table state. */
  const filterValue = (id: string) =>
    (columnFilters.find((filter) => filter.id === id)?.value as string | undefined) ?? '';
  /** Converts a table sort state into the direction expected by the API. */
  const sortValue = (id: string): 'asc' | 'desc' | undefined => {
    const sort = sorting.find((item) => item.id === id);
    return sort ? (sort.desc ? 'desc' : 'asc') : undefined;
  };
  /** Reads a multi-select filter value from the table state. */
  const filterValues = (id: string): string[] => {
    const value = columnFilters.find((filter) => filter.id === id)?.value;

    if (Array.isArray(value)) {
      return value.map(String);
    }

    return typeof value === 'string' && value ? [value] : [];
  };
  const filename = filterValue('filename');
  const comment = filterValue('comment');
  const companyName = filterValue('company');
  const userFullname = filterValue('createdByFullname');
  const statuses = filterValues('status');
  const createdAtAfter = filterValue('createdAtAfter');
  const createdAtBefore = filterValue('createdAtBefore');
  const filenameSort = sortValue('filename');
  const createdAtSort = sortValue('createdAt');
  const statusSort = sortValue('status');
  const parsedCreatedAtBefore = createdAtBefore ? parseISO(createdAtBefore) : null;
  const inclusiveCreatedAtBefore =
    parsedCreatedAtBefore && isValid(parsedCreatedAtBefore)
      ? format(addDays(parsedCreatedAtBefore, 1), 'yyyy-MM-dd')
      : '';

  return {
    page,
    perPage,
    ...(filename ? { filename } : {}),
    ...(comment ? { comment } : {}),
    ...(companyName ? { 'createdBy.company.name': companyName } : {}),
    ...(userFullname ? { 'createdBy.user.fullname': userFullname } : {}),
    ...(statuses.length > 0 ? { 'status[]': statuses } : {}),
    ...(createdAtAfter ? { 'createdAt[after]': createdAtAfter } : {}),
    ...(inclusiveCreatedAtBefore ? { 'createdAt[strictly_before]': inclusiveCreatedAtBefore } : {}),
    ...(filenameSort ? { 'order[filename]': filenameSort } : {}),
    ...(createdAtSort ? { 'order[createdAt]': createdAtSort } : {}),
    ...(statusSort ? { 'order[status]': statusSort } : {})
  };
}
